import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { portalConst } from '@app/config';
import { AlertService, LoadingService, NotificationService } from '@app/core';
import { ContentType } from '@app/core/models/content-type.enum';
import { CourseContent } from '@app/core/models/course-content';
import { CertificateService } from '@app/core/services/certificate.service';
import { SubdivionService } from '@app/core/services/subdivisions.service';
import { ModalCertificatePreviewComponent } from '@app/modules/learning-trails/components/modal-certificate-preview/modal-certificate-preview.component';
import { ModalCertificateComponent } from '@app/modules/learning-trails/components/modal-certificate/modal-certificate.component';
import { SidenavGroupComponent } from '@app/shared/modules/template/components/sidenav';
import { notNull, safeEmpty, selectedEntityActive } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { sortBy, toNumber } from 'lodash';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { BehaviorSubject, finalize, map, switchMap, tap } from 'rxjs';
import { StudioFilterService } from '../../services';
import { StudioService } from '../../services/studio.service';
import { ModalCertificateFormComponent } from '../modal-certificate-form/modal-certificate-form.component';
import { ModalProductsVoucherComponent } from '../modal-products-voucher/modal-products-voucher.component';

@Component({
  selector: 'app-modal-courses',
  templateUrl: './modal-courses.component.html',
  standalone: false,
})
export class ModalCoursesComponent implements AfterViewInit, OnDestroy, OnInit {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly certificateService = inject(CertificateService);
  private readonly notificationService = inject(NotificationService);
  private readonly alertService = inject(AlertService);
  private readonly destroy = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogRef = inject(DialogRef);
  private readonly dialog = inject(Dialog);
  private readonly loadingService = inject(LoadingService);
  private readonly studioService = inject(StudioService);
  private readonly studioFilterService = inject(StudioFilterService);
  private readonly subdivionService = inject(SubdivionService);
  private readonly cdref = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  public readonly data = inject(DIALOG_DATA);

  public isScreenHeightLessThan780: boolean = false;

  @ViewChild(SidenavGroupComponent, { static: true }) public sidenav: SidenavGroupComponent;
  public readonly ckeditorUploadImageUrl = `${portalConst.api.usuario}/v2/upload/ckeditor-image?module=trilhas`;
  private readonly pathTempFile: string = portalConst.s3_path_temp;
  public form = this.formBuilder.nonNullable.group({
    titulo: [''],
    descricao: [''],
    thumbnail: [''],
    status: [false],
    emite_certificado: [false],
    voucher_productos: [false],
    sugerir_certificado: [false],
    sugestao_certificado_id: [undefined as number | undefined],
    sugestao_certificado: [undefined as any],
    products: [undefined as any],
    id: [undefined as number | undefined],
    hash: [undefined as string | undefined],
    colecao_id: [null as number | null],
    categoria_id: [null as number | null],
    pais_id: [1],
    entidades: [undefined as any],
  });

  public formSubdivision = this.formBuilder.nonNullable.group({
    id: [undefined as number | undefined],
    subdivisao_id: [0],
    curso_id: [undefined as number | undefined],
    nome: [''],
    thumbnail: [''],
    hash: [undefined as string | undefined],
    fixar: [false],
    ordem: [0],
  });

  public submittedCourse$ = new BehaviorSubject<boolean>(false);
  public submittedSubdivision$ = new BehaviorSubject<boolean>(false);
  public opened$ = new BehaviorSubject<boolean>(false);
  public thumbInvalid$ = new BehaviorSubject<boolean>(false);
  public subThumbInvalid$ = new BehaviorSubject<boolean>(false);
  public items$ = new BehaviorSubject<CourseContent[]>(undefined);
  public dataCertificate = new BehaviorSubject<any[]>([]);
  public thumbnailLink: string;
  public subthumbnailLink: string;
  public subdivisions = [];
  public subdivision: CourseContent;
  public trails = [];
  public config: DropzoneConfigInterface = {
    clickable: true,
    url: `${portalConst.api.usuario}/temp-upload/v1/file`,
    params: {
      savePhoto: true,
      largura: 500,
      altura: 500,
      limiteMaximo: true,
      pesoMaximo: 150,
      stringKbMb: 'kb',
    },
    createImageThumbnails: false,
    maxFiles: 1,
    maxFilesize: 0.15,
    acceptedFiles: '.jpg,.jpeg,.png',
    previewsContainer: false,
  };

  public get descricaoControl(): FormControl {
    return this.form.get('descricao') as FormControl;
  }

  constructor() {
    this.initFormCourse();
    this.initFormSubdivision();

    if (!!this.data?.course?.id) {
      this.thumbnailLink = this.data?.course?.thumbnail;
      this.getCourseBySlug();
      this.getSubdivisions();
    } else {
      this.certificateService.passCertificate(undefined);
    }

    this.store
      .select(AppSelectors.ActiveUser)
      .pipe(notNull(), takeUntilDestroyed(this.destroy))
      .subscribe((user) => {
        this.form.get('entidades').setValue(selectedEntityActive(user));
      });

    this.certificateService.certificate$.pipe(notNull()).subscribe((certificate) => this.AddCertificate(certificate));
    this.studioFilterService.subdivision$.pipe(notNull()).subscribe((subdivision) => this.AddSubdivision(subdivision));
    this.studioFilterService.items$.pipe(notNull()).subscribe((data) => this.makeTree(data));
  }

  public ngOnInit() {
    this.checkScreenHeight();
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: any) {
    this.checkScreenHeight();
  }

  public checkScreenHeight() {
    this.isScreenHeightLessThan780 = window.innerHeight < 780;
  }

  public ngAfterViewInit() {
    this.loadListeners();
  }

  public ngOnDestroy(): void {
    this.loadingService.finish();
  }

  public onAddTrail(): void {
    this.router.navigate([new URL(window.location.href).pathname, 'trilha', 'cadastro', this.form.value.id]);
    this.dialogRef.close({ reset: true });
  }

  private initFormCourse() {
    this.form = this.formBuilder.nonNullable.group({
      titulo: [
        this.data?.course?.titulo ?? '',
        [Validators.required, Validators.maxLength(70), Validators.minLength(3)],
      ],

      descricao: [this.data?.course?.descricao ?? '', [Validators.required]],

      thumbnail: [this.data?.course?.thumbnail ?? '', [Validators.required]],

      status: [!!this.data?.course?.status as boolean],

      emite_certificado: [false as boolean],
      voucher_productos: [false as boolean],
      sugerir_certificado: [false as boolean],

      sugestao_certificado_id: [undefined as number | undefined],
      sugestao_certificado: [undefined as any],
      products: [undefined as any],

      id: [this.data?.course?.id ?? undefined],

      hash: [undefined as string | undefined],

      colecao_id: [this.data?.collection?.id ?? null, [Validators.required]],

      categoria_id: [this.data?.collection?.categoria_id ?? null, [Validators.required]],

      pais_id: [this.data?.course?.pais_id ?? 1],

      entidades: [undefined as any],
    });

    this.form.controls.titulo.valueChanges.subscribe(() => {
      this.form.controls.titulo.markAsTouched();
      this.form.controls.titulo.markAsPristine();
    });

    this.form.controls.id.valueChanges.pipe(notNull(), takeUntilDestroyed(this.destroy)).subscribe(() => {
      this.formSubdivision.patchValue({ curso_id: this.form.controls.id.value }, { emitEvent: false });
    });

    this.form.controls.categoria_id.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe(() => {
      this.form.patchValue({ colecao_id: null });
    });

    this.form.controls.voucher_productos.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe((checked) => {
      if (!checked || this.data.products.length === 0) {
        this.form.controls.products.setValue(undefined);
        return;
      }

      if (this.data.products.length > 1) {
        this.openModalVoucher();
      } else {
        this.form.controls.products.setValue([this.data.products[0].id]);
      }
    });
  }

  private initFormSubdivision() {
    this.formSubdivision = this.formBuilder.nonNullable.group({
      id: [undefined as number | undefined],

      subdivisao_id: [0],

      curso_id: [this.data?.course?.id ?? undefined, [Validators.required]],

      nome: ['', [Validators.required, Validators.maxLength(70)]],

      thumbnail: ['', [Validators.required]],

      hash: [undefined as string | undefined],

      fixar: [false as boolean],
      ordem: [0],
    });
  }

  public getCourseBySlug() {
    this.studioService
      .showCourseBySlug(this.data.course.slug)
      .pipe(takeUntilDestroyed(), notNull())
      .subscribe({
        next: (resp) => {
          const newCourse = {
            titulo: resp.data.titulo,
            descricao: resp.data.descricao,
            thumbnail: resp.data.thumbnail,
            status: !!resp.data.status,
            emite_certificado: !!resp.data.emite_certificado,
            sugerir_certificado: !!resp.data.sugerir_certificado,
            sugestao_certificado_id: resp.data.sugestao_certificado_id,
            sugestao_certificado: resp.data.sugestao_certificado,
            voucher_productos: !!resp.data.produtos && resp.data.produtos.length > 0,
            products: resp.data.produtos.map((p: any) => p.id),
            id: resp.data.id,
            colecao_id: resp.data.colecao_id,
            categoria_id: resp.data.categoria_id,
            pais_id: resp.data.pais_id,
            entidades: resp.data.entidades,
          };
          this.form.patchValue(newCourse, { emitEvent: false });
        },
        error: (e) => this.alertService.alert({ message: e.error.msg }),
      });
  }

  public getSubdivisions() {
    this.subdivionService
      .getSubdivision(this.data.course.id)
      .pipe(
        takeUntilDestroyed(),
        notNull(),
        map((resp) => resp.data),
      )
      .subscribe({
        next: (data) => {
          this.makeTree(data);
        },
        error: (e) => this.alertService.alert({ message: e.error.msg }),
      });
  }

  public makeTree(data) {
    this.trails = data.trilhas;
    if (data.subdivisoes.length > 0) {
      this.subdivisions = data.subdivisoes;
      this.loadTree(data.subdivisoes);
    } else {
      this.items$.next(data.trilhas);
    }
  }

  public loadTree(subdivisoes) {
    this.items$.next(this.treeBuild(subdivisoes));
    this.searchTrails(this.items$.value);
  }

  public treeBuild(list) {
    const treeList = [];
    const lookup = {};
    list.forEach((obj) => {
      lookup[obj['id']] = obj;
      obj['items'] = [];
      obj['fixar'] = !!obj['fixar'];
    });
    list.forEach((obj) => {
      if (obj['subdivisao_id'] !== 0) {
        lookup[obj['subdivisao_id']]['items'].push(obj);
      } else {
        treeList.push(obj);
      }
    });
    return treeList;
  }

  public searchTrails(items) {
    items.forEach((value) => {
      if (value.items.length > 0) {
        this.searchTrails(value.items);
      } else {
        value.items = this.getTrails(this.trails, value.id);
        value.trilhas = value.items.length;
      }
    });
  }

  public pushItem(item: CourseContent, items: CourseContent[]) {
    items.forEach((obj) => {
      if (obj.id === item.subdivisao_id) {
        obj.items.push(item);
      } else {
        this.pushItem(item, obj.items);
      }
    });
  }

  public replaceItem(item: CourseContent, items: CourseContent[]) {
    for (let i = 0; i < items.length; i++) {
      if (items[i].id === item.id) {
        items[i] = item;
        this.cdref.markForCheck();
        return;
      }
      this.replaceItem(item, items[i].items);
    }
  }

  public getTrails(trilhas, subdivisao_id) {
    return trilhas.filter((item) => item.subdivisao_id === subdivisao_id);
  }

  public categoryCollections(category_id: any) {
    return this.data.categories.find((cat) => cat.id === category_id).colecoes;
  }

  public isSubdivisao(item: CourseContent): boolean {
    return item.tipo === ContentType.Subdivisao;
  }

  public hasSubdivisions() {
    return this.items$.value.filter((item) => item.tipo === ContentType.Subdivisao).length > 0;
  }

  public isTrilha(item: CourseContent): boolean {
    return item.tipo === ContentType.Trilha;
  }

  public hasTrails() {
    return this.items$.value.filter((item) => item.tipo === ContentType.Trilha).length > 0;
  }

  public enableSendOnUploadThumbnail(data) {
    if (!!data.finished) {
      this.thumbInvalid$.next(false);
      this.submittedCourse$.next(false);
    }
  }

  public disableSendUploadThumbnail(data) {
    if (!!data.uploading) {
      this.thumbInvalid$.next(true);
    }
  }

  public enableSendOnUploadThumbnailSubdivision(data) {
    if (!!data.finished) {
      this.subThumbInvalid$.next(false);
      this.submittedSubdivision$.next(false);
    }
  }

  public disableSendUploadThumbnailSubdivision(data) {
    if (!!data.uploading) {
      this.subThumbInvalid$.next(true);
    }
  }

  public onUploadThumbnailCourse(event) {
    this.form.get('hash').setValue(event.hash);
    this.form.get('thumbnail').setValue(event.file);
    this.thumbnailLink = this.pathTempFile + event.file;
    this.form.markAsDirty();
    this.form.markAsTouched();
  }

  public onUploadThumbnailSubdivision(event) {
    this.formSubdivision.get('hash').setValue(event.hash);
    this.formSubdivision.get('thumbnail').setValue(event.file);
    this.subthumbnailLink = this.pathTempFile + event.file;
    this.formSubdivision.markAsDirty();
    this.formSubdivision.markAsTouched();
  }

  public onSubmit() {
    this.submittedCourse$.next(true);
    if (this.form.valid) {
      this.save();
    } else {
      this.form.markAllAsTouched();
    }
  }

  private save() {
    this.alertService
      .confirm({ message: this.translateService.instant(`trilhas.estudio.curso.deseja-salvar`) })
      .pipe(
        finalize(() => this.submittedCourse$.next(false)),
        notNull(),
        tap(() => this.loadingService.start()),
        switchMap(() =>
          !!this.form.value.id
            ? this.studioService.updateCourse(this.form.value.id, this.form.value).pipe(safeEmpty())
            : this.studioService.postCourse(this.form.value).pipe(safeEmpty()),
        ),
        tap(() => this.loadingService.finish()),
      )
      .subscribe({
        next: (course) => {
          const newCourse = {
            categoria_id: toNumber(course.categoria_id),
            colecao_id: toNumber(course.colecao_id),
            descricao: course.descricao,
            emite_certificado: !!course.emite_certificado,
            id: course.id,
            pais_id: toNumber(course.pais_id),
            slug: course.slug,
            status: !!course.status,
            sugestao_certificado_id: course.sugestao_certificado_id,
            thumbnail: course.thumbnail,
            titulo: course.titulo,
            vitrines: course.vitrines,
          };
          this.form.patchValue(newCourse, { emitEvent: false });
          this.formSubdivision.patchValue({ curso_id: course.id }, { emitEvent: false });
          this.studioFilterService.passCourse(newCourse, this.data.collection);
          this.notificationService.success('geral.sucesso');
          this.dialogRef.close(newCourse);
        },
        error: (e) => {
          this.alertService.alert({ message: e.error.msg });
        },
      });
  }

  public onSubmitSubdivision() {
    this.submittedSubdivision$.next(true);
    if (this.formSubdivision.valid) {
      this.saveSubdivision();
    } else {
      this.formSubdivision.markAllAsTouched();
    }
  }

  private saveSubdivision() {
    this.alertService
      .confirm({ message: this.translateService.instant(`trilhas.estudio.subdivisao.deseja-salvar`) })
      .pipe(
        finalize(() => this.submittedSubdivision$.next(false)),
        tap(() => {
          if (
            !this.formSubdivision.value.id &&
            this.formSubdivision.value.subdivisao_id === 0 &&
            this.formSubdivision.value.ordem === 0
          ) {
            this.formSubdivision.get('ordem').setValue(this.items$.value.length);
          }
        }),
        notNull(),
        tap(() => this.loadingService.start()),
        switchMap(() =>
          !!this.formSubdivision.value.id
            ? this.subdivionService.updateSubdivision(this.formSubdivision.value.id, this.formSubdivision.value)
            : this.subdivionService.postSubdivision(this.formSubdivision.value),
        ),
        tap(() => this.loadingService.finish()),
      )
      .subscribe({
        next: (resp) => {
          this.notificationService.success('geral.sucesso');

          const item: CourseContent = {
            ...this.formSubdivision.getRawValue(),
            id: resp['id'],
            trilhas: this.subdivision?.trilhas || 0,
            items: this.subdivision?.items || [],
            tipo: ContentType.Subdivisao,
            thumbnail: resp['thumbnail'],
          };

          const subdivision = this.formSubdivision.getRawValue();

          if (subdivision.subdivisao_id === 0) {
            if (!!subdivision.id) {
              this.items$.next(this.items$.value.map((i) => (i.id === subdivision.id ? item : i)));
            } else {
              this.items$.next([
                ...this.items$.value,
                {
                  ...subdivision,
                  id: resp['id'],
                  trilhas: 0,
                  items: [],
                  tipo: ContentType.Subdivisao,
                  thumbnail: resp['thumbnail'],
                },
              ]);
            }
          } else if (!!subdivision.id) {
            this.replaceItem(item, this.items$.value);
          } else {
            this.pushItem(item, this.items$.value);
          }

          this.formSubdivision.reset({
            id: undefined,
            subdivisao_id: 0,
            curso_id: this.form.value.id,
            nome: undefined,
            thumbnail: undefined,
            hash: undefined,
            fixar: false,
            ordem: 0,
          });
          this.subthumbnailLink = '';
          this.opened$.next(!this.opened$.value);
        },
        error: (e) => {
          this.alertService.alert({ message: e.error.msg });
        },
      });
  }

  public onDelete() {
    this.alertService
      .confirm({ message: `trilhas.estudio.curso.deseja-excluir` })
      .pipe(
        notNull(),
        switchMap(() => this.studioService.destroyCourse(this.form.value.id)),
      )
      .subscribe({
        next: () => {
          this.studioFilterService.removeCourse(this.form.value.id, this.data.collection);
          this.dialogRef.close();
          this.notificationService.success('geral.deletado');
        },
        error: (e) => this.alertService.alert({ message: e.error.msg }),
      });
  }

  public onAddCertificate() {
    this.dialog
      .open(ModalCertificateComponent, {
        hasBackdrop: true,
      })
      .closed.pipe(notNull())
      .subscribe();
  }

  public verifyCamposCertificado() {
    if (this.form.value.emite_certificado === false || this.form.value.sugerir_certificado === false) {
      this.form.patchValue({
        sugerir_certificado: false,
        sugestao_certificado_id: null,
        sugestao_certificado: null,
      });
    }
  }

  public onClose() {
    this.dialogRef.close();
  }

  public edit() {
    this.dialog
      .open(ModalCertificateFormComponent, {
        hasBackdrop: true,
        data: this.onData(),
      })
      .closed.pipe(notNull())
      .subscribe();
  }

  public onOpenModalCertificado(certificadoId: any) {
    this.dialog
      .open(ModalCertificatePreviewComponent, {
        hasBackdrop: true,
        data: { certificadoId, data: this.onData() },
      })
      .closed.pipe(notNull())
      .subscribe();
  }

  public closeSidenav() {
    this.formSubdivision.reset({
      id: undefined,
      subdivisao_id: 0,
      curso_id: this.form.value.id,
      nome: undefined,
      thumbnail: undefined,
      hash: undefined,
      fixar: false,
      ordem: 0,
    });
    this.subthumbnailLink = '';
    this.subThumbInvalid$.next(false);
    this.submittedSubdivision$.next(false);
    this.opened$.next(false);
  }

  public openSidenav() {
    this.opened$.next(true);
  }

  public onData() {
    return {
      id: this.form.value.sugestao_certificado_id,
      title: this.form.value.sugestao_certificado.titulo,
      text: this.form.value.sugestao_certificado.texto,
      backgroundImage:
        this.form.value.sugestao_certificado.background || 'assets/images/certificate-default/certificado-padrao.jpg',
      assinaturas: [
        {
          assinatura: this.form.value.sugestao_certificado.file_assinatura_1,
          nome: this.form.value.sugestao_certificado.nome_assinatura_1,
          linha_1: this.form.value.sugestao_certificado.linha_1_assinatura_1,
          linha_2: this.form.value.sugestao_certificado.linha_2_assinatura_1,
        },
        {
          assinatura: this.form.value.sugestao_certificado.file_assinatura_2,
          nome: this.form.value.sugestao_certificado.nome_assinatura_2,
          linha_1: this.form.value.sugestao_certificado.linha_1_assinatura_2,
          linha_2: this.form.value.sugestao_certificado.linha_2_assinatura_2,
        },
        {
          assinatura: this.form.value.sugestao_certificado.file_assinatura_3,
          nome: this.form.value.sugestao_certificado.nome_assinatura_3,
          linha_1: this.form.value.sugestao_certificado.linha_1_assinatura_3,
          linha_2: this.form.value.sugestao_certificado.linha_2_assinatura_3,
        },
      ],
    };
  }

  private openModalVoucher() {
    this.dialog
      .open<boolean>(ModalProductsVoucherComponent, {
        data: { products: this.data.products },
      })
      .closed.pipe(
        notNull(),
        map((products: any) => products.map((product) => product.id)),
      )
      .subscribe((products: any) => {
        this.form.controls.products.setValue(products);
      });
  }

  private AddCertificate(certificate) {
    this.form.patchValue({
      sugestao_certificado_id: certificate.id,
      sugestao_certificado: certificate,
    });

    this.cdref.markForCheck();
  }

  private AddSubdivision(subdivision) {
    this.opened$.next(true);
    this.subthumbnailLink = subdivision.thumbnail;
    this.subdivision = subdivision;
    this.formSubdivision.reset({
      curso_id: subdivision.curso_id,
      fixar: subdivision.fixar,
      id: subdivision.id,
      nome: subdivision.nome,
      ordem: subdivision.ordem,
      subdivisao_id: subdivision.subdivisao_id,
      thumbnail: subdivision.thumbnail,
    });
    this.studioFilterService.passSubdivision(undefined);
  }

  public reorder(data: any) {
    const brotherItem = this.items$.value.find((item) => item.ordem === data.order);
    const item = this.items$.value.find((item) => item.id === data.item_id);
    brotherItem.ordem = item.ordem;
    item.ordem = data.order;
    this.items$.next(sortBy(this.items$.value, ['ordem']));
  }

  public onChangeCharacter() {
    const name = this.clearStr(this.form.value.titulo);
    this.form.controls.titulo.setValue(name);
  }

  public onChangeCharacterSubdivision() {
    const name = this.clearStr(this.formSubdivision.value.nome);
    this.formSubdivision.controls.nome.setValue(name);
  }

  private clearStr(str) {
    if (!!str) {
      str = str.replace(/@/gi, '');
    }

    return str;
  }

  private loadListeners() {
    this.sidenav.end.closed$.pipe(takeUntilDestroyed(this.destroy)).subscribe(() => this.closeSidenav());
  }
}
